import type { MarketModel, SimInputs, SimResult } from '../engine/types'

/**
 * Two tiers of fidelity.
 *
 * `preview` runs on every slider frame and must stay well inside a frame budget
 * on a mid-range Android. `full` is debounced to when the thumb settles.
 * Because paths are seeded per index, the preview is a true prefix of the full
 * run — so when the full result lands the fan refines in place instead of
 * snapping to a different shape.
 */
export const PREVIEW_PATHS = 2_000
export const FULL_PATHS = 10_000

/**
 * Which series a request belongs to — a scenario id in the product, anything
 * stable in principle. Deliberately a plain string: the worker has no business
 * knowing what a scenario is, and `sim.worker.ts` must stay importable without
 * dragging the product model into it.
 */
export type SimKey = string

/** The single-series key, so callers that never compare need not think about it. */
export const SOLO_KEY: SimKey = 'solo'

export interface SimRequest {
  /**
   * Coalescing is **per key**, not global. With one series a newer request
   * replacing an older one is exactly right; with two it would mean scenario B's
   * request evicted scenario A's the moment it arrived, and A's fan would freeze
   * at whatever it last computed.
   */
  key: SimKey
  /** Monotonic within a key. Stamped onto the reply so the UI can drop stale results. */
  generation: number
  inputs: SimInputs
  model: MarketModel
  paths: number
  seed: number
}

export interface SimResponse {
  key: SimKey
  generation: number
  result: SimResult
  /** Wall-clock time the worker spent, for the perf overlay on /dev. */
  elapsedMs: number
}

/**
 * The typed arrays inside a SimResult, so they can be handed to postMessage as
 * transferables. Moving ~1.5 KB of buffers is cheap either way, but transferring
 * avoids a structured clone on every single frame of a drag.
 */
export function transferablesOf(result: SimResult): ArrayBuffer[] {
  return [
    result.ages.buffer,
    result.bands.p10.buffer,
    result.bands.p25.buffer,
    result.bands.p50.buffer,
    result.bands.p75.buffer,
    result.bands.p90.buffer,
    result.requiredMedian.buffer,
    result.successByAge.buffer,
  ] as ArrayBuffer[]
}
